import {useState, useCallback, useMemo, type CSSProperties, type ChangeEvent} from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CategoryIcon from '@mui/icons-material/Category'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setBodyColor,
  setBodyMetalness,
  setBodyRoughness,
  setBodyClearcoat,
  setBodyClearcoatRoughness,
  setBodyReflectivity,
  setBodyNoiseIntensity,
  setBodyNormalStrength,
  setBodyNormalPattern,
  setButtonsNoiseIntensity,
  setButtonsNormalStrength,
  setButtonsNormalPattern,
  setFrontFaceColor,
  setFrontFaceMetalness,
  setFrontFaceRoughness,
  setFrontFaceClearcoat,
  setFrontFaceClearcoatRoughness,
  setFrontFaceReflectivity,
  setButtonsColor,
  setButtonsMetalness,
  setButtonsRoughness,
  setButtonsClearcoat,
  setButtonsClearcoatRoughness,
  setButtonsReflectivity,
  setAntennasColor,
  setAntennasMetalness,
  setAntennasRoughness,
  setAntennasClearcoat,
  setAntennasClearcoatRoughness,
  setAntennasReflectivity,
  setScrewsColor,
  setScrewsMetalness,
  setScrewsRoughness,
  setScrewsClearcoat,
  setScrewsClearcoatRoughness,
  setScrewsReflectivity,
  setFrontSpeakerColor,
  setFrontSpeakerMetalness,
  setFrontSpeakerRoughness,
  setFrontSpeakerClearcoat,
  setFrontSpeakerClearcoatRoughness,
  setFrontSpeakerReflectivity,
  setSpeakerGrillColor,
  setSpeakerGrillMetalness,
  setSpeakerGrillRoughness,
  setSpeakerGrillClearcoat,
  setSpeakerGrillClearcoatRoughness,
  setSpeakerGrillReflectivity,
  setChargingConnectorTongueColor,
  setChargingConnectorTongueMetalness,
  setChargingConnectorTongueRoughness,
  setChargingConnectorTongueClearcoat,
  setChargingConnectorTongueClearcoatRoughness,
  setChargingConnectorTongueReflectivity,
  setChargingConnectorRimColor,
  setChargingConnectorRimMetalness,
  setChargingConnectorRimRoughness,
  setChargingConnectorRimClearcoat,
  setChargingConnectorRimClearcoatRoughness,
  setChargingConnectorRimReflectivity,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import type {BodyNormalPattern} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {RootState} from '../../../common/store'
import type {PayloadAction} from '@reduxjs/toolkit'
import SliderRow from '../sharedWidgets/SliderRow'

// ── Types ─────────────────────────────────────────────────────────

interface MaterialGroup {
  id: string
  titleKey: string
  colorSelector: (s: RootState) => string
  metalnessSelector: (s: RootState) => number
  roughnessSelector: (s: RootState) => number
  clearcoatSelector: (s: RootState) => number
  clearcoatRoughnessSelector: (s: RootState) => number
  reflectivitySelector: (s: RootState) => number
  setColor: (v: string) => PayloadAction<string>
  setMetalness: (v: number) => PayloadAction<number>
  setRoughness: (v: number) => PayloadAction<number>
  setClearcoat: (v: number) => PayloadAction<number>
  setClearcoatRoughness: (v: number) => PayloadAction<number>
  setReflectivity: (v: number) => PayloadAction<number>
}

const MATERIAL_GROUPS: MaterialGroup[] = [
  {
    id: 'body',
    titleKey: 'device.materials.body',
    colorSelector: (s) => selectActiveScene(s).bodyColor,
    metalnessSelector: (s) => selectActiveScene(s).bodyMetalness,
    roughnessSelector: (s) => selectActiveScene(s).bodyRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).bodyClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).bodyClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).bodyReflectivity,
    setColor: setBodyColor,
    setMetalness: setBodyMetalness,
    setRoughness: setBodyRoughness,
    setClearcoat: setBodyClearcoat,
    setClearcoatRoughness: setBodyClearcoatRoughness,
    setReflectivity: setBodyReflectivity,
  },
  {
    id: 'frontFace',
    titleKey: 'device.materials.frontFace',
    colorSelector: (s) => selectActiveScene(s).frontFaceColor,
    metalnessSelector: (s) => selectActiveScene(s).frontFaceMetalness,
    roughnessSelector: (s) => selectActiveScene(s).frontFaceRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).frontFaceClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).frontFaceClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).frontFaceReflectivity,
    setColor: setFrontFaceColor,
    setMetalness: setFrontFaceMetalness,
    setRoughness: setFrontFaceRoughness,
    setClearcoat: setFrontFaceClearcoat,
    setClearcoatRoughness: setFrontFaceClearcoatRoughness,
    setReflectivity: setFrontFaceReflectivity,
  },
  {
    id: 'buttons',
    titleKey: 'device.materials.buttons',
    colorSelector: (s) => selectActiveScene(s).buttonsColor,
    metalnessSelector: (s) => selectActiveScene(s).buttonsMetalness,
    roughnessSelector: (s) => selectActiveScene(s).buttonsRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).buttonsClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).buttonsClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).buttonsReflectivity,
    setColor: setButtonsColor,
    setMetalness: setButtonsMetalness,
    setRoughness: setButtonsRoughness,
    setClearcoat: setButtonsClearcoat,
    setClearcoatRoughness: setButtonsClearcoatRoughness,
    setReflectivity: setButtonsReflectivity,
  },
  {
    id: 'antennas',
    titleKey: 'device.materials.antennas',
    colorSelector: (s) => selectActiveScene(s).antennasColor,
    metalnessSelector: (s) => selectActiveScene(s).antennasMetalness,
    roughnessSelector: (s) => selectActiveScene(s).antennasRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).antennasClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).antennasClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).antennasReflectivity,
    setColor: setAntennasColor,
    setMetalness: setAntennasMetalness,
    setRoughness: setAntennasRoughness,
    setClearcoat: setAntennasClearcoat,
    setClearcoatRoughness: setAntennasClearcoatRoughness,
    setReflectivity: setAntennasReflectivity,
  },
  {
    id: 'screws',
    titleKey: 'device.materials.screws',
    colorSelector: (s) => selectActiveScene(s).screwsColor,
    metalnessSelector: (s) => selectActiveScene(s).screwsMetalness,
    roughnessSelector: (s) => selectActiveScene(s).screwsRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).screwsClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).screwsClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).screwsReflectivity,
    setColor: setScrewsColor,
    setMetalness: setScrewsMetalness,
    setRoughness: setScrewsRoughness,
    setClearcoat: setScrewsClearcoat,
    setClearcoatRoughness: setScrewsClearcoatRoughness,
    setReflectivity: setScrewsReflectivity,
  },
  {
    id: 'frontSpeaker',
    titleKey: 'device.materials.frontSpeaker',
    colorSelector: (s) => selectActiveScene(s).frontSpeakerColor,
    metalnessSelector: (s) => selectActiveScene(s).frontSpeakerMetalness,
    roughnessSelector: (s) => selectActiveScene(s).frontSpeakerRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).frontSpeakerClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).frontSpeakerClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).frontSpeakerReflectivity,
    setColor: setFrontSpeakerColor,
    setMetalness: setFrontSpeakerMetalness,
    setRoughness: setFrontSpeakerRoughness,
    setClearcoat: setFrontSpeakerClearcoat,
    setClearcoatRoughness: setFrontSpeakerClearcoatRoughness,
    setReflectivity: setFrontSpeakerReflectivity,
  },
  {
    id: 'speakerGrill',
    titleKey: 'device.materials.speakerGrill',
    colorSelector: (s) => selectActiveScene(s).speakerGrillColor,
    metalnessSelector: (s) => selectActiveScene(s).speakerGrillMetalness,
    roughnessSelector: (s) => selectActiveScene(s).speakerGrillRoughness,
    clearcoatSelector: (s) => selectActiveScene(s).speakerGrillClearcoat,
    clearcoatRoughnessSelector: (s) => selectActiveScene(s).speakerGrillClearcoatRoughness,
    reflectivitySelector: (s) => selectActiveScene(s).speakerGrillReflectivity,
    setColor: setSpeakerGrillColor,
    setMetalness: setSpeakerGrillMetalness,
    setRoughness: setSpeakerGrillRoughness,
    setClearcoat: setSpeakerGrillClearcoat,
    setClearcoatRoughness: setSpeakerGrillClearcoatRoughness,
    setReflectivity: setSpeakerGrillReflectivity,
  },
]

// ── Compound material groups (multiple sub-materials in one accordion) ──

interface CompoundMaterialGroup {
  id: string
  titleKey: string
  subGroups: {subTitleKey: string; group: MaterialGroup}[]
}

const COMPOUND_MATERIAL_GROUPS: CompoundMaterialGroup[] = [
  {
    id: 'chargingConnector',
    titleKey: 'device.materials.chargingConnector',
    subGroups: [
      {
        subTitleKey: 'device.materials.chargingConnectorTongue',
        group: {
          id: 'chargingConnectorTongue',
          titleKey: 'device.materials.chargingConnectorTongue',
          colorSelector: (s) => selectActiveScene(s).chargingConnectorTongueColor,
          metalnessSelector: (s) => selectActiveScene(s).chargingConnectorTongueMetalness,
          roughnessSelector: (s) => selectActiveScene(s).chargingConnectorTongueRoughness,
          clearcoatSelector: (s) => selectActiveScene(s).chargingConnectorTongueClearcoat,
          clearcoatRoughnessSelector: (s) => selectActiveScene(s).chargingConnectorTongueClearcoatRoughness,
          reflectivitySelector: (s) => selectActiveScene(s).chargingConnectorTongueReflectivity,
          setColor: setChargingConnectorTongueColor,
          setMetalness: setChargingConnectorTongueMetalness,
          setRoughness: setChargingConnectorTongueRoughness,
          setClearcoat: setChargingConnectorTongueClearcoat,
          setClearcoatRoughness: setChargingConnectorTongueClearcoatRoughness,
          setReflectivity: setChargingConnectorTongueReflectivity,
        },
      },
      {
        subTitleKey: 'device.materials.chargingConnectorRim',
        group: {
          id: 'chargingConnectorRim',
          titleKey: 'device.materials.chargingConnectorRim',
          colorSelector: (s) => selectActiveScene(s).chargingConnectorRimColor,
          metalnessSelector: (s) => selectActiveScene(s).chargingConnectorRimMetalness,
          roughnessSelector: (s) => selectActiveScene(s).chargingConnectorRimRoughness,
          clearcoatSelector: (s) => selectActiveScene(s).chargingConnectorRimClearcoat,
          clearcoatRoughnessSelector: (s) => selectActiveScene(s).chargingConnectorRimClearcoatRoughness,
          reflectivitySelector: (s) => selectActiveScene(s).chargingConnectorRimReflectivity,
          setColor: setChargingConnectorRimColor,
          setMetalness: setChargingConnectorRimMetalness,
          setRoughness: setChargingConnectorRimRoughness,
          setClearcoat: setChargingConnectorRimClearcoat,
          setClearcoatRoughness: setChargingConnectorRimClearcoatRoughness,
          setReflectivity: setChargingConnectorRimReflectivity,
        },
      },
    ],
  },
]

// ── Reusable inner controls (color + 5 sliders) ───────────────────

interface MaterialControlsProps {
  group: MaterialGroup
}

const MaterialControls = ({group}: MaterialControlsProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const color = useAppSelector(group.colorSelector)
  const metalness = useAppSelector(group.metalnessSelector)
  const roughness = useAppSelector(group.roughnessSelector)
  const clearcoat = useAppSelector(group.clearcoatSelector)
  const clearcoatRoughness = useAppSelector(group.clearcoatRoughnessSelector)
  const reflectivity = useAppSelector(group.reflectivitySelector)

  const handleColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(group.setColor(e.target.value))
    },
    [dispatch, group],
  )
  const handleMetalnessChange = useCallback((v: number) => dispatch(group.setMetalness(v)), [dispatch, group])
  const handleRoughnessChange = useCallback((v: number) => dispatch(group.setRoughness(v)), [dispatch, group])
  const handleClearcoatChange = useCallback((v: number) => dispatch(group.setClearcoat(v)), [dispatch, group])
  const handleClearcoatRoughnessChange = useCallback(
    (v: number) => dispatch(group.setClearcoatRoughness(v)),
    [dispatch, group],
  )
  const handleReflectivityChange = useCallback((v: number) => dispatch(group.setReflectivity(v)), [dispatch, group])
  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const sx = useMemo(() => buildGroupStyles(tokens), [tokens])

  return (
    <>
      <Box sx={sx.row}>
        <Typography sx={sx.label}>{t('device.materials.color')}</Typography>
        <input type="color" value={color} onChange={handleColorChange} onBlur={handleSealUndo} style={sx.colorInput} />
      </Box>
      <SliderRow
        label={t('device.materials.metalness')}
        value={metalness}
        min={0}
        max={1}
        step={0.01}
        unit=""
        onChange={handleMetalnessChange}
        onChangeCommitted={handleSealUndo}
      />
      <SliderRow
        label={t('device.materials.roughness')}
        value={roughness}
        min={0}
        max={1}
        step={0.01}
        unit=""
        onChange={handleRoughnessChange}
        onChangeCommitted={handleSealUndo}
      />
      <SliderRow
        label={t('device.materials.clearcoat')}
        value={clearcoat}
        min={0}
        max={1}
        step={0.01}
        unit=""
        onChange={handleClearcoatChange}
        onChangeCommitted={handleSealUndo}
      />
      <SliderRow
        label={t('device.materials.clearcoatRoughness')}
        value={clearcoatRoughness}
        min={0}
        max={1}
        step={0.01}
        unit=""
        onChange={handleClearcoatRoughnessChange}
        onChangeCommitted={handleSealUndo}
      />
      <SliderRow
        label={t('device.materials.reflectivity')}
        value={reflectivity}
        min={0}
        max={1}
        step={0.01}
        unit=""
        onChange={handleReflectivityChange}
        onChangeCommitted={handleSealUndo}
      />
    </>
  )
}

// ── Single material group sub-section ─────────────────────────────

interface MaterialGroupSectionProps {
  group: MaterialGroup
  expanded: boolean
  onToggle: () => void
}

const MaterialGroupSection = ({group, expanded, onToggle}: MaterialGroupSectionProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const hasTexture = group.id === 'body' || group.id === 'buttons'
  const noiseIntensity = useAppSelector((s) =>
    group.id === 'buttons' ? selectActiveScene(s).buttonsNoiseIntensity : selectActiveScene(s).bodyNoiseIntensity,
  )
  const normalStrength = useAppSelector((s) =>
    group.id === 'buttons' ? selectActiveScene(s).buttonsNormalStrength : selectActiveScene(s).bodyNormalStrength,
  )
  const normalPattern = useAppSelector((s) =>
    group.id === 'buttons' ? selectActiveScene(s).buttonsNormalPattern : selectActiveScene(s).bodyNormalPattern,
  )

  const handleNoiseChange = useCallback(
    (value: number) => {
      dispatch(group.id === 'buttons' ? setButtonsNoiseIntensity(value) : setBodyNoiseIntensity(value))
    },
    [dispatch, group.id],
  )

  const handleNormalChange = useCallback(
    (value: number) => {
      dispatch(group.id === 'buttons' ? setButtonsNormalStrength(value) : setBodyNormalStrength(value))
    },
    [dispatch, group.id],
  )

  const handlePatternChange = useCallback(
    (e: SelectChangeEvent) => {
      dispatch(
        group.id === 'buttons'
          ? setButtonsNormalPattern(e.target.value as BodyNormalPattern)
          : setBodyNormalPattern(e.target.value as BodyNormalPattern),
      )
      dispatch(sealUndo())
    },
    [dispatch, group.id],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const sx = useMemo(() => buildGroupStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square expanded={expanded} onChange={onToggle} sx={sx.groupAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.groupSummary}>
        <Typography sx={sx.groupTitle}>{t(group.titleKey)}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.groupDetails}>
        <MaterialControls group={group} />

        {hasTexture && (
          <>
            <SliderRow
              label={t('device.materials.noiseIntensity')}
              value={noiseIntensity}
              min={0}
              max={1}
              step={0.01}
              unit=""
              onChange={handleNoiseChange}
              onChangeCommitted={handleSealUndo}
            />
            <SliderRow
              label={t('device.materials.normalStrength')}
              value={normalStrength}
              min={0}
              max={1}
              step={0.01}
              unit=""
              onChange={handleNormalChange}
              onChangeCommitted={handleSealUndo}
            />
            {normalStrength > 0 && (
              <Box sx={sx.row}>
                <Typography sx={sx.label}>{t('device.materials.normalPattern')}</Typography>
                <Select
                  value={normalPattern}
                  onChange={handlePatternChange}
                  size="small"
                  variant="outlined"
                  sx={sx.patternSelect}
                >
                  <MenuItem value="brushed">{t('device.materials.brushed')}</MenuItem>
                  <MenuItem value="noise">{t('device.materials.noise')}</MenuItem>
                </Select>
              </Box>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

// ── Compound material group sub-section ───────────────────────────

interface CompoundMaterialGroupSectionProps {
  group: CompoundMaterialGroup
  expanded: boolean
  onToggle: () => void
}

const CompoundMaterialGroupSection = ({group, expanded, onToggle}: CompoundMaterialGroupSectionProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildGroupStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square expanded={expanded} onChange={onToggle} sx={sx.groupAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.groupSummary}>
        <Typography sx={sx.groupTitle}>{t(group.titleKey)}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.groupDetails}>
        {group.subGroups.map((sub, idx) => (
          <Box key={sub.group.id} sx={idx === 0 ? sx.subGroupFirst : sx.subGroup}>
            <Typography sx={sx.subGroupTitle}>{t(sub.subTitleKey)}</Typography>
            <MaterialControls group={sub.group} />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

// ── DeviceMaterialsPanel — materials sub-accordion ────────────────

const DeviceMaterialsPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const [expandedGroup, setExpandedGroup] = useState<string | false>(false)

  const handleToggle = useCallback(
    (id: string) => () => {
      setExpandedGroup((prev) => (prev === id ? false : id))
    },
    [],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square sx={sx.subAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.subSummary}>
        <CategoryIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('device.materials.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.subDetails}>
        {MATERIAL_GROUPS.map((group) => (
          <Box key={group.id} sx={sx.groupCard}>
            <MaterialGroupSection
              group={group}
              expanded={expandedGroup === group.id}
              onToggle={handleToggle(group.id)}
            />
          </Box>
        ))}
        {COMPOUND_MATERIAL_GROUPS.map((group) => (
          <Box key={group.id} sx={sx.groupCard}>
            <CompoundMaterialGroupSection
              group={group}
              expanded={expandedGroup === group.id}
              onToggle={handleToggle(group.id)}
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles — outer sub-accordion ──────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '0 4px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  groupCard: {
    borderRadius: '6px',
    border: `1px solid ${tokens.accordion.border}`,
    overflow: 'hidden',
  },
})

// ── Styles — material group section ───────────────────────────────

const buildGroupStyles = (tokens: ThemeTokens) => ({
  groupAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  groupSummary: {
    minHeight: 32,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 32},
    '& .MuiAccordionSummary-content': {
      margin: '4px 0',
      '&.Mui-expanded': {margin: '4px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 16,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
  },
  groupDetails: {
    padding: '4px 8px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  subGroupFirst: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  subGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    paddingTop: '8px',
    marginTop: '4px',
    borderTop: `1px solid ${tokens.accordion.border}`,
  },
  subGroupTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: tokens.text.secondary,
    marginBottom: '2px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  label: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
    minWidth: 70,
    flexShrink: 0,
  },
  colorInput: {
    width: '100%',
    height: 28,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: 6,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 2,
  } as CSSProperties,
  patternSelect: {
    flex: 1,
    fontSize: 11,
    color: tokens.text.primary,
    '& .MuiSelect-select': {
      padding: '4px 8px',
      fontSize: 11,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
})

export default DeviceMaterialsPanel
